import axios from "axios";
import { getCookie } from "../getcookie.js";

const apiUrl = process.env.REACT_APP_API_URL;
const adminApiUrl = process.env.REACT_APP_ADMIN_API_URL;
const appEnvironment = process.env.REACT_APP_ENVIRONMENT;
const isProductionEnvironment = appEnvironment === "production";
const isDevelopmentEnvironment = appEnvironment === "development";
const isHttpsOnly = process.env.REACT_APP_HTTPS_ONLY === "true";
/**
 * VAPT Security Enhancements:
 * - Fresh token retrieval for each request
 * - Security headers (X-Content-Type-Options, X-Frame-Options)
 * - Safe error handling without information leakage
 * - Request/response timeout configuration
 * - CSRF protection via credentials
 */

// Helper function to get headers with fresh token and security headers
const getHeaders = () => ({
  "Content-Type": "application/json; charset=UTF-8",
  Accept: "application/json",
  ...(isHttpsOnly ? { "Access-Control-Allow-Origin": process.env.REACT_APP_ACCESS_CONTROL_ALLOW_ORIGIN } : {}),
  ...(!isHttpsOnly ? { Authorization: getCookie("mawalog") } : {}),
  /* "X-Content-Type-Options": "nosniff",
  "X-Frame-Options": "DENY", */
});

// Validate endpoint to prevent injection
const validateEndpoint = (endpoint) => {
  if (!endpoint || typeof endpoint !== "string") return "";
  // Remove dangerous characters that could be used for path traversal
  return endpoint.replace(/\.\./g, "").replace(/[<>"']/g, "");
};

// Helper function for API calls with VAPT compliance
export const apiCall = async (method, endpoint, data = null, options = {}) => {
  try {
    // Validate endpoint
    const safeEndpoint = validateEndpoint(endpoint);
    if (!safeEndpoint) {
      return {
        data: {
          success: false,
          message: "Invalid request endpoint",
        },
      };
    }

    const config = {
      method,
      url: `${apiUrl}${safeEndpoint}`,
      withCredentials: isHttpsOnly,
      headers: getHeaders(),
      

      ...options,
      
    };
    //timeout: 30000, // 30 second timeout
    // withCredentials: true, // Enable CSRF protection
    if (data) {
      config.data = data;
    }

    const response = await axios(config);
    return response;
  } catch (error) {
    // Return safe error response without leaking internal details
    if (error.response) {
      if (
        error.response.status === 401 && !endpoint.includes("login") && !endpoint.includes("sign_in")
      )
        handleUnauthorized();
      return error.response;
    } else if (error.request) {
      return {
        data: {
          success: false,
          message: "Network error. Please check your connection.",
        },
      };
    } else {
      return {
        data: {
          success: false,
          message: "An error occurred. Please try again.",
        },
      };
    }
  }
};

export const apiCallAdmin = async (
  method,
  endpoint,
  data = null,
  options = {},
) => {
  try {
    // Validate endpoint
    const safeEndpoint = validateEndpoint(endpoint);
    if (!safeEndpoint) {
      return {
        data: {
          success: false,
          message: "Invalid request endpoint",
        },
      };
    }

    const config = {
      method,
      url: `${adminApiUrl}${safeEndpoint}`,
      withCredentials: isHttpsOnly,
      headers: getHeaders(),
      

      ...options,
      
    };
    if (data) {
      config.data = data;
    }

    const response = await axios(config);
    return response;
  } catch (error) {
    if (error.response) {
      if (
        error.response.status === 401 &&
        !endpoint.includes("login") &&
        !endpoint.includes("sign_in")
      )
        handleUnauthorized();
      return error.response;
    } else if (error.request) {
      return {
        data: {
          success: false,
          message: "Network error. Please check your connection.",
        },
      };
    } else {
      return {
        data: {
          success: false,
          message: "An error occurred. Please try again.",
        },
      };
    }
  }
};

export const apiCallGeneric = async (
  method,
  fullUrl,
  data = null,
  options = {},
) => {
  try {
    const config = {
      method,
      url: fullUrl,
      withCredentials: isHttpsOnly,
      headers: getHeaders(),

      ...options,
      
    };
    if (data) {
      config.data = data;
    }

    const response = await axios(config);
    return response;
  } catch (error) {
    if (error.response) {
      if (
        error.response.status === 401 &&
        !fullUrl.includes("login") &&
        !fullUrl.includes("sign_in")
      )
        handleUnauthorized();
      return error.response;
    } else if (error.request) {
      return {
        data: {
          success: false,
          message: "Network error. Please check your connection.",
        },
      };
    } else {
      return {
        data: {
          success: false,
          message: "An error occurred. Please try again.",
        },
      };
    }
  }
};

/**
 * Handles Unauthorized (401) errors by clearing tokens and redirecting to login.
 */
const handleUnauthorized = () => {
  const accountsUrl = process.env.REACT_APP_ACCOUNTS_URL;

  // Clear tokens/cookies
  document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie = "mawalog=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  const currentURL = window.location.href;
  const redirectData = {
    redirectPath: currentURL,
    app: "accounts",
    ts: Date.now(),
  };

  // Safe tab-specific storage for post-login redirect
  window.name = JSON.stringify(redirectData);
  window.location.href = `${accountsUrl}`;
};
